import React from 'react';
import { Link } from 'react-router-dom';
import { Collapse, Tag } from 'antd';
import { FiCalendar, FiRefreshCw } from 'react-icons/fi';
import styles from '@pages/api-portal/styles.module.scss';
import { MethodBadge } from '@pages/api-portal/method-badge';
import { StatusBadge } from '@pages/api-portal/status-badge';
import { CatalogGroupIcon } from '@pages/api-portal/catalog-group-icon';
import { formatCatalogDate } from '@pages/api-portal/utils';
import { ApiEndpoint, ApiGroup } from '@pages/api-portal/types';

interface EndpointGroupsListProps {
    groupedEndpoints: Record<string, ApiEndpoint[]>;
    apiGroups: ApiGroup[];
    expandedGroups: string[];
    onToggleGroup: (groupId: string) => void;
}

/*
 * Accordion list of API groups and their endpoint cards, grouped and filtered by the
 * catalog's active filters.
 */
export function EndpointGroupsList({ groupedEndpoints, apiGroups, expandedGroups, onToggleGroup }: EndpointGroupsListProps) {
    const groupKeys = Object.keys(groupedEndpoints);
    if (groupKeys.length === 0) return null;

    return (
        <Collapse activeKey={expandedGroups} onChange={(keys) => {
            const nextKeys = Array.isArray(keys) ? keys : [keys];
            groupKeys.forEach((key) => {
                const wasExpanded = expandedGroups.includes(key);
                const isExpanded = nextKeys.includes(key);
                if (wasExpanded !== isExpanded) onToggleGroup(key);
            });
        }}>
            {groupKeys.map((groupKey) => {
                const endpoints = groupedEndpoints[groupKey];
                const group = apiGroups.find((g) => g.groupKey === groupKey || g.name === groupKey);
                return (
                    <Collapse.Panel
                        key={groupKey}
                        header={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div className={styles.iconTile}>
                                    <CatalogGroupIcon icon={group?.icon ?? ''} />
                                </div>
                                <span>
                                    <strong style={{ textTransform: 'capitalize', color: '#fff', fontSize: 14 }}>{groupKey}</strong>{' '}
                                    <Tag>{endpoints.length} endpoints</Tag>
                                    {group?.description && (
                                        <div className={styles.mutedText} style={{ fontSize: 12 }}>
                                            {group.description}
                                        </div>
                                    )}
                                </span>
                            </div>
                        }
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {endpoints.map((endpoint) => (
                                <Link key={endpoint.id} to={`/api-portal/catalog/${endpoint.id}`}>
                                    <div className={styles.cardOnPanel}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <MethodBadge method={endpoint.method} />
                                            <strong style={{ color: '#fff', fontSize: 14 }}>{endpoint.name}</strong>
                                            {endpoint.status && endpoint.status !== 'stable' && (
                                                <StatusBadge status={endpoint.status} />
                                            )}
                                        </div>
                                        <div className={styles.cardDivider} />
                                        <code className={styles.mutedText} style={{ display: 'block', marginBottom: 6 }}>
                                            {endpoint.path}
                                        </code>
                                        <p className={styles.mutedText} style={{ margin: 0 }}>{endpoint.description}</p>
                                        <div className={styles.cardDivider} />
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                flexWrap: 'wrap',
                                                gap: 8,
                                            }}
                                        >
                                            <div>
                                                {endpoint.tags.slice(0, 3).map((tag) => (
                                                    <Tag key={tag}>{tag}</Tag>
                                                ))}
                                            </div>
                                            <div
                                                className={styles.mutedText}
                                                style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 12, whiteSpace: 'nowrap' }}
                                            >
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <FiCalendar /> Created:{' '}
                                                    <strong style={{ color: '#fff' }}>{formatCatalogDate(endpoint.created)}</strong>
                                                </span>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <FiRefreshCw /> Updated:{' '}
                                                    <strong style={{ color: '#fff' }}>{formatCatalogDate(endpoint.updated)}</strong>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </Collapse.Panel>
                );
            })}
        </Collapse>
    );
}
